import { ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
import { FIELD_TYPES,DynamicFormArray, Props } from '../../model/form.model';
import { LAYOUT_TYPE } from '../../model/common.model';
import { CommonUtils } from '../../utils/common.utils';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, ExportConfig, SearchConfig } from '../../model/api.model';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { EliteCertificationExportSearchConfig, EliteCertificationReportFormArray, EliteCertificationReportProps, EliteCertificationReportSearchConfig, EliteCertificationReportTableSchema, EliteCertificationDataSchema, EliteDynamicOptionFields } from '../../model/elite-certification.model';
import { CommonService } from '../../services/common.service';
import { JobHistoryDetailComponent } from '../job-history-detail/job-history-detail.component';
import { FieldNames, FieldTypesForOptions } from '../../model/form-fields.model';
import { JobHistroyDataSchema } from '../../model/job-history.model';
import { PaginatorService } from '../../services/paginator-service.service';
import { EXCEL_EXTENSION } from '../../services/excel-export.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-elite-certification-report',
  standalone: true,
  imports: [PageContainerComponent, JobHistoryDetailComponent],
  templateUrl: './elite-certification-report.component.html',
})
export class EliteCertificationReportComponent extends BaseComponent implements OnDestroy {
  protected data = 'EliteCertificationReportComponent';
  public dataTableSchema: DataTableSchema = structuredClone(EliteCertificationReportTableSchema);
  public filterSchema: DynamicFormArray = structuredClone(EliteCertificationReportFormArray);
  protected props: Props = {
    ...structuredClone(EliteCertificationReportProps),
    isBreadCrumbSection: true,
  }
  readonly LayoutType: typeof LAYOUT_TYPE = LAYOUT_TYPE;
  public mode: 'View' | 'List' = 'List';
  public dataSet: any = {};
  title = "Elite Certification";
  commonUtils = inject(CommonUtils);
  service = inject(CommonService);
  public searchConfig = structuredClone(EliteCertificationReportSearchConfig)
  eliteDynamicOptionFields = EliteDynamicOptionFields;
  private isBackNavigationInProgress = false;
  private readonly paginatorService = inject(PaginatorService);
  private readonly changeRef = inject(ChangeDetectorRef);
  private isFirstLoad = true;
  private isDataInitialized = false;
  private currentPage: any;
  public tableFilter = '';
  private readonly destroy$ = new Subject<void>();

 override ngOnInit() {
    super.ngOnInit();
    super.commonFunction();
    if (this.dataTableSchema['titleSchema']) {
      this.dataTableSchema['titleSchema']['title'] = '';
    }
    this.updateTableCallbacks();
    this.dataTableSchema.filterCallback = this.paginationFilterCallback
    this.dataTableSchema.sortCallback = this.sortCallback;
    this.dataTableSchema.exportCallback = this.exportCallback;
    this.disableEnableButtons(false);
    this.dataTableSchema.dataSource = [];
    this.paginatorService.pageEvent$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (this.isFirstLoad) {
        this.isFirstLoad = false;
        if (this.dataTableSchema['pageSizeOptions']['pageSize']) {
          this.dataTableSchema['pageSizeOptions']['pageLength'] =
            this.dataTableSchema['pageSizeOptions']['pageSize'][0];
        }
      } else {
        this.currentPage = event;
        this.dataTableSchema.dataSource = [];
        this.setTableLoadingState(true);
        const searchConfig = this.getSearchConfig();
        if (searchConfig) {
          this.fetchAllData(searchConfig);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  override onResetClicked = (source?: 'user' | 'navigation') => {
    const parseDate = (val: any) => {
      if (!val) { return '' }
      if (val instanceof Date) { return val }
      const s = String(val);
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
        const [mm, dd, yyyy] = s.split('/').map(Number);
        return new Date(yyyy, mm - 1, dd);
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return new Date(s);
      }
      return s;
    }
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map((field) => {
      const def = field['defaultValue'] ?? '';
      const value = (field['fieldType'] === FIELD_TYPES.DATE) ? parseDate(def) : (def ? def : '');
      return { ...field, selectedValue: value };
    });
    this.dataTableSchema = {
      ...this.dataTableSchema,
      inputFilter: '',
      dataSource: []
    };
    this.resetDataTable();
    this.resetSorting();
    this.isDataInitialized = false;
    this.disableEnableButtons(false);
    if (source !== 'navigation') {
      if (this.filterSchema?.autoSearchOnLoad) {
        this.onSearchClicked(structuredClone(this.searchConfig));
      }
    }
  }
resetDataTable() {
    this.dataTableSchema.dataSource = [];
    if (this.dataTableSchema?.pageSizeOptions) {
      if (this.dataTableSchema?.pageSizeOptions.pageSize) {
        this.currentPage = {
          page: 0,
          pageSize: Number(this.currentPage?.pageSize) || this.dataTableSchema?.pageSizeOptions.pageSize[0],
        };
      }
    }
    this.dataTableSchema['pageSizeOptions']['pageLength'] = 0;
  }

  updateTableCallbacks() {
    this.dataTableSchema.columnSchema.forEach((col) => {
      if (col['fieldKey'] === EliteCertificationDataSchema[FieldNames['SpinId']]) {
        col['onClick'] = this.columnOnClick;
      }
    });
  }

  columnOnClick = (event: any) => {
    const spinId = event?.[EliteCertificationDataSchema[FieldNames['SpinId']]] || event?.[JobHistroyDataSchema[FieldNames['SpinId']]];
    const associateTypeRaw = event?.[JobHistroyDataSchema['associateType']] || event?.associateType || 'LEXUS';
    const associateType = String(associateTypeRaw).toUpperCase();

    this.dataSet = {
      ...event,
      [JobHistroyDataSchema[FieldNames['SpinId']]]: spinId,
      [JobHistroyDataSchema['associateType']]: associateType
    };
    this.mode = 'View';
  };

  onDetailViewChange(_event: any): void {
    this.isBackNavigationInProgress = true;
    this.mode = 'List';
    this.setTableLoadingState(true);
    this.dataTableSchema = {
      ...this.dataTableSchema,
      inputFilter: '',
      dataSource: []
    };
    this.onSearchClicked(structuredClone(this.searchConfig));
  }

 override  onSearchClicked = (searchParam: any) => {
    if (searchParam) {
      this.resetSorting()
      this.resetDataTable();
    }
    const searchConfig = this.getSearchConfig()
    this.fetchAllData(searchConfig)
  };
  paginationFilterCallback = (filterValue: string) => {
    if (this.isDataInitialized) {
      if (filterValue || (!filterValue && this.tableFilter)) {
        const searchConfig = this.getSearchConfig();
        this.tableFilter = filterValue;
        this.resetDataTable();
        this.fetchAllData(searchConfig)
      }
    }
  }
  exportCallback = () => {
    const searchConfig = this.getSearchConfig();
    searchConfig['body']['download'] = true
    this.fetchAllData(searchConfig, true)
  }
  sortCallback = (fieldInfo: Array<Record<string, string>>) => {
    if (fieldInfo) {
      const sortBy: any = {}
      fieldInfo.forEach(field => {
        sortBy[field['id']] = field['direction']
      })
      this.sortBy = sortBy;
      if (this.dataTableSchema.dataSource.length > 0) {
        const searchConfig = this.getSearchConfig();
        this.resetDataTable()
        this.fetchAllData(searchConfig);
      }
    }
  }
    getSearchConfig = () => {
      const searchConfig = structuredClone(EliteCertificationReportSearchConfig)
      this.filterSchema['FieldsArray'].forEach(field => {
        if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
          if (Number.isNaN(field['selectedValue']) || Array.isArray(field['selectedValue'])) {
            searchConfig['body'][field['fieldName']] = field['selectedValue']
          } else {
            searchConfig['body'][field['fieldName']] = field['selectedValue'].toString();
          }
        } else if (field['fieldType'] === FIELD_TYPES.NUMBER) {
          searchConfig['body'][field['fieldName']] = '';
        }
      });
      if (this.dataTableSchema.inputFilter.length > 0) {
        searchConfig['body']['searchText'] = this.dataTableSchema.inputFilter
      }
      if (this.sortBy && Object.keys(this.sortBy).length > 0) {
        if (!searchConfig['body']) {
          searchConfig['body'] = {}
        }
        searchConfig['body']['sortBy'] = this.sortBy;
      }
      return searchConfig
    }

  fetchAllData = (_searchConfig: SearchConfig, callFromExport = false) => {
    this.setTableLoadingState(true);
    const searchConfig = structuredClone(_searchConfig);
    if (!callFromExport) {
      searchConfig['body'].page = Number(this.currentPage?.pageIndex ?? 0) + 1;
      searchConfig['body'].pageSize = Number(this.currentPage?.pageSize) || this.dataTableSchema.pageSizeOptions?.pageSize?.[0];
    }
    const exportQueryConfig = this.getExportConfig();
    const exportConfig: ExportConfig = {
      tableSchema: this.dataTableSchema,
      environment: ApiEnvNames['StaffMaster'],
      fileExtension: EXCEL_EXTENSION,
      searchParameters: EliteCertificationExportSearchConfig,
      exportFileName: 'Elite_Certification_Report',
    };
    if (callFromExport) {
      searchConfig['body']['exportConfig'] = exportQueryConfig;
    }
    return this.service.getDataFromDataSource(searchConfig, ApiEnvNames['EliteCertification']).then(async response => {
      if (response['status'] === API_STATUS['Success'] && Array.isArray(response.result)) {
        if (callFromExport) {
          await this.service.downloadExcelFromS3(response.result[0], exportConfig);
        } else {
          this.dataTableSchema['pageSizeOptions']['pageLength'] = response?.result?.[0]?.totalRecordsCount ?? 0;
          this.dataTableSchema.dataSource = response.result;
          if (!this.isDataInitialized && response.result.length > 0) {
            this.isDataInitialized = true;
          }
        }
      } else if (callFromExport) {
        await this.service.recursiveCallS3(exportQueryConfig, exportConfig);
      } else {
                this.setTableLoadingState(false);
        this.dataTableSchema.dataSource = [];
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
      }
      this.setTableLoadingState(false);
      this.changeRef.detectChanges();
    }).catch(err => {
      this.dataTableSchema.dataSource = [];
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
    }).finally(() => {
      this.setTableLoadingState(false);
      if (this.isBackNavigationInProgress) {
        this.commonUtils.spinner('hide');
        this.isBackNavigationInProgress = false;
      }
    })
  };

  getExportConfig() {
    return {
      fileName: `Elite_Certification_Report_Export_${this.commonUtils.getFormattedDate()}.csv`,
      bucketName: this.commonUtils.getBucketName()
    };
  }

  override updateOptions = () => {
    const fieldNames = new Set(this.eliteDynamicOptionFields.fields.map((p: any) => p.name));
    let apiResult = {} as any;
    const updateFieldWithOptions = () => {
      if (this.filterSchema.FieldsArray.length > 0) {
        this.filterSchema.FieldsArray.forEach((field) => {
          if (FieldTypesForOptions.includes(field['fieldType']) && fieldNames.has(field['fieldName'])) {
            field['isLoading'] = false;
            field['options'] = apiResult[field['fieldName']] ?? [];
          }
        });
      }
    }
    return this.service.getDataFromDataSource(this.eliteDynamicOptionFields.searchConfig, ApiEnvNames['EliteCertification'])?.then(response => {
      if (response['status'] === API_STATUS['Success']) {
        if (Array.isArray(response.result) && response.result.length > 0) {
          const normalizedRows = Array.isArray(response.result[0]?.payload) ? response.result[0].payload : response.result;
          apiResult = this.fillValuesFromApi(normalizedRows);
          updateFieldWithOptions();
        } else {
          updateFieldWithOptions();
        }
      } else {
        updateFieldWithOptions();
        console.info(`${ERROR_RESPONSE['layoutErrorPrefix']}`, response['errorMsg']);
      }
    }).catch(err => {
      updateFieldWithOptions();
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
    })
  }

  fillValuesFromApi = (response: any[]) => {
    const result: Record<string, Array<any>> = {
      [FieldNames.LexusPrimaryJobCode]: []
    };
    if (Array.isArray(response) && response.length > 0) {
      result[FieldNames.LexusPrimaryJobCode] = response
        .filter((item: any) => item?.lexusPrimaryJobCode)
        .map((item: any) => ({ fieldLabel: item.lexusPrimaryJobCode, fieldValue: item.lexusPrimaryJobCode }));
    }
    return result;
  }

}